import { Popover, Spin } from 'antd';

import { usePhoneClientMenu } from '@app/components/RecordModal/components/RecordDetails/hooks/usePhoneClientMenu';
import { usePhoneClientMenuEffects } from '@app/components/RecordModal/components/RecordDetails/hooks/usePhoneClientMenuEffects';
import type { RecordClientSearchResult } from '@app/components/RecordModal/components/RecordDetails/types';
import { ClientSearchOption } from '@app/components/RecordModal/components/RecordDetails/ui/ClientSearchOption';

import type { FC, ReactNode } from 'react';

import '@app/components/RecordModal/components/RecordDetails/styles/PhoneClientMenu.scss';

interface PhoneClientMenuProps {
  clients: RecordClientSearchResult[];
  open: boolean;
  isLoading: boolean;
  isSuccess: boolean;
  onSelect(clientId: number): void;
  children: ReactNode;
}

// Клиенты, найденные по номеру из поля «Мобильный телефон» (task/11629):
// загрузка, строки клиентов или «Клиенты не найдены», как у поля «Поиск».
// Клиент выбирается только кликом. Меню не ловит Tab: фокус остаётся в
// поле телефона и уходит в следующее поле.
export const PhoneClientMenu: FC<PhoneClientMenuProps> = ({
  clients,
  open,
  isLoading,
  isSuccess,
  onSelect,
  children,
}) => {
  const {
    containerRef,
    menuWidth,
    updateMenuWidthHandler,
    menuMouseDownHandler,
    getOptionClickHandler,
  } = usePhoneClientMenu({ onSelect });

  usePhoneClientMenuEffects({ open, updateMenuWidthHandler });

  return (
    <Popover
      open={open && (isLoading || isSuccess)}
      trigger={[]}
      placement="bottomLeft"
      arrow={false}
      rootClassName="PhoneClientMenu__popover"
      styles={{ root: { width: menuWidth || undefined } }}
      content={
        <div
          className="PhoneClientMenu__list"
          role="listbox"
          onMouseDown={menuMouseDownHandler}
          data-testid="phone-client-menu"
        >
          {isLoading && (
            <div
              className="PhoneClientMenu__empty"
              data-testid="phone-client-loading"
            >
              <Spin size="small" />
            </div>
          )}
          {!isLoading && !clients.length && (
            <div
              className="PhoneClientMenu__empty"
              data-testid="phone-client-empty"
            >
              Клиенты не найдены
            </div>
          )}
          {!isLoading &&
            clients.map((client) => (
              <div
                key={client.Id}
                className="PhoneClientMenu__option"
                role="option"
                aria-selected={false}
                onClick={getOptionClickHandler(client.Id)}
              >
                <ClientSearchOption
                  client={client}
                  dataTestId="phone-client-option"
                />
              </div>
            ))}
        </div>
      }
    >
      <div className="PhoneClientMenu" ref={containerRef}>
        {children}
      </div>
    </Popover>
  );
};
